import React from "react";
import { BookOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { universityCourseCardClassName, universityCourseThumbClassName, universityCourseThumbFallbackClassName } from "./universityCardShell";

export type UniversityCourseCardModel = {
  id: number;
  title: string;
  thumbnailGradient: string;
  thumbnail?: string;
  modules?: number;
  lessons?: number;
  progress?: number;
  sanityId?: string;
  description?: string;
};

type UniversityCourseCardProps = {
  course: UniversityCourseCardModel;
  variant: "enrolled" | "catalog" | "completed";
  locked?: boolean;
  onPrimaryAction: () => void;
};

function CourseProgressBar({ value }: { value: number }) {
  const clamped = Math.min(100, Math.max(0, value));

  return (
    <div
      data-university-progress=""
      className="h-2 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full bg-[image:var(--gradient-primary)] transition-[width]"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function MetaRow({
  modules,
  lessons,
  progress,
}: {
  modules?: number;
  lessons?: number;
  progress?: number;
}) {
  if (!modules && !lessons) return null;

  const remainingModules =
    typeof progress === "number" && modules
      ? Math.ceil(modules * ((100 - progress) / 100))
      : modules;
  const remainingLessons =
    typeof progress === "number" && lessons
      ? Math.ceil(lessons * ((100 - progress) / 100))
      : lessons;
  const modulesLabel =
    typeof progress === "number" ? `${remainingModules} modules left` : `${modules} modules`;
  const lessonsLabel =
    typeof progress === "number" ? `${remainingLessons} lessons left` : `${lessons} lessons`;

  return (
    <div
      data-university-course-meta=""
      className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground"
    >
      {modules ? (
        <span className="inline-flex items-center gap-1">
          <BookOpen className="size-3.5 shrink-0" aria-hidden />
          {modulesLabel}
        </span>
      ) : null}
      {lessons ? (
        <span className="inline-flex items-center gap-1">
          <BookOpen className="size-3.5 shrink-0" aria-hidden />
          {lessonsLabel}
        </span>
      ) : null}
    </div>
  );
}

export function UniversityCourseCard({
  course,
  variant,
  locked = false,
  onPrimaryAction,
}: UniversityCourseCardProps): React.ReactElement {
  const thumbnailSrc = course.thumbnail;

  return (
    <Card
      data-university-course-card=""
      className={cn(universityCourseCardClassName, locked && "opacity-60")}
    >
      {thumbnailSrc ? (
        <img
          src={thumbnailSrc}
          alt=""
          width={1200}
          height={400}
          draggable={false}
          data-university-course-thumb=""
          className={universityCourseThumbClassName}
        />
      ) : (
        <div
          data-university-course-thumb=""
          className={universityCourseThumbFallbackClassName}
          style={{ backgroundImage: course.thumbnailGradient }}
        />
      )}
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <CardTitle
          data-university-course-title=""
          className="font-[family-name:var(--font-primary)] text-[color:var(--brand-tropical-surf)]"
        >
          {course.title}
        </CardTitle>

        {variant === "enrolled" ? (
          <>
            <CourseProgressBar value={course.progress ?? 0} />
            <p data-university-course-muted="" className="text-xs text-muted-foreground">
              {course.progress ?? 0}% completed
            </p>
            <MetaRow
              modules={course.modules}
              lessons={course.lessons}
              progress={course.progress}
            />
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="mt-auto w-full"
              onClick={onPrimaryAction}
            >
              <span className="normal-case tracking-normal">Continue learning</span>
            </Button>
          </>
        ) : null}

        {variant === "catalog" ? (
          <>
            <MetaRow modules={course.modules} lessons={course.lessons} />
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              className="mt-auto w-full"
              disabled={locked}
              onClick={onPrimaryAction}
            >
              <span className="normal-case tracking-normal">
                {locked ? "Locked" : "View course"}
              </span>
            </Button>
          </>
        ) : null}

        {variant === "completed" ? (
          <>
            <p data-university-course-muted="" className="text-xs text-muted-foreground">
              100% completed
            </p>
            <Button
              type="button"
              variant="outline-brand"
              size="sm"
              data-font="primary"
              className="mt-auto w-full"
              onClick={onPrimaryAction}
            >
              <span className="normal-case tracking-normal">View certificate</span>
            </Button>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
